import React from "react";
import { useTheme } from "../contexts/ThemeContext";

interface SelectableTableRowProps {
  appearance?: "default" | "alpha";
  /** Whether this row is currently selected */
  isSelected: boolean;
  /** Click handler for row selection */
  onClick: () => void;
  /** Child elements (td cells) */
  children: React.ReactNode;
  /** Optional additional className */
  className?: string;
  /** Optional test id for testing */
  testId?: string;
}

/**
 * A reusable table row component with consistent selection styling.
 * Use this for admin tables that work with SplitScreenLayout.
 * 
 * Features:
 * - Prominent left border indicator when selected
 * - Blue-tinted background when selected
 * - Hover state for unselected rows
 * - Smooth transition animation
 * - Dark/light mode support
 */
const SelectableTableRow: React.FC<SelectableTableRowProps> = ({
  appearance = "default",
  isSelected,
  onClick,
  children,
  className = "",
  testId,
}) => {
  const { theme } = useTheme();
  const isDarkMode = theme === "dark";

  const baseStyles = "cursor-pointer transition-colors";
  
  const selectedStyles = appearance === "alpha" ? "bg-v7-paper border-l-4 border-l-v7-ink/25" : isDarkMode
    ? "bg-rp-surface-raised border-l-4 border-l-rp-amber"
    : "bg-blue-100 border-l-4 border-l-blue-600";
  
  const unselectedStyles = appearance === "alpha" ? "hover:bg-v7-paper border-l-4 border-l-transparent" : isDarkMode
    ? "hover:bg-rp-surface-raised border-l-4 border-l-transparent"
    : "hover:bg-gray-50 border-l-4 border-l-transparent";

  const rowClassName = `${baseStyles} ${isSelected ? selectedStyles : unselectedStyles} ${className}`.trim();

  return (
    <tr
      className={rowClassName}
      onClick={onClick}
      data-testid={testId}
      data-selected={isSelected}
    >
      {children}
    </tr>
  );
};

export default SelectableTableRow;

